import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Phone, ChevronRight, MessageCircle } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Contact } from "../components/Contact";
import { WhatsAppFloat } from "../components/WhatsAppFloat";
import { useI18n } from "../context/I18nContext";
import { resolveImageUrl } from "../components/ImageUploader";
import { getSettings, getProducts } from "../lib/store";

async function tryFetch(url) {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export default function ProductDetail() {
  const { slug } = useParams();
  const { lang, t } = useI18n();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [settings, setSettings] = useState(getSettings);
  const [relatedProducts, setRelatedProducts] = useState([]);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const hasBackend = Boolean(backendUrl);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setActive(0);

    // Always read from store first (admin panel data)
    const allProducts = getProducts();
    const storeProd = allProducts.find(p => p.slug === slug && p.lang === lang && p.active);

    if (storeProd) {
      setProduct(storeProd);
      setRelatedProducts(allProducts.filter(p => p.slug !== slug && p.lang === lang && p.active));
      setSettings(getSettings());
      setLoading(false);
    } else if (hasBackend) {
      Promise.all([
        tryFetch(`${backendUrl}/api/products/${slug}?lang=${lang}`),
        tryFetch(`${backendUrl}/api/settings`),
        tryFetch(`${backendUrl}/api/products?lang=${lang}`),
      ]).then(([prod, sett, allProds]) => {
        if (prod) setProduct(prod); else setNotFound(true);
        if (sett) setSettings(s => ({ ...s, ...sett }));
        if (allProds) setRelatedProducts(allProds.filter(p => p.slug !== slug));
        setLoading(false);
      });
    } else {
      setNotFound(true);
      setLoading(false);
    }

    window.scrollTo(0, 0);
  }, [slug, lang, hasBackend, backendUrl]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f6f6f6]">
        <Navbar />
        <div className="max-w-[1200px] mx-auto px-4 py-32 text-center text-[#0d1b2a]/50 text-sm">
          Yükleniyor…
        </div>
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="min-h-screen bg-[#f6f6f6]">
        <Navbar />
        <div className="max-w-[1200px] mx-auto px-4 py-32 text-center">
          <h1 className="text-3xl font-black text-[#0d1b2a]" style={{ fontFamily: "Chivo, Inter, sans-serif" }}>
            Ürün bulunamadı
          </h1>
          <Link to="/" className="btn-primary mt-6 inline-flex">
            <ArrowLeft size={16} /> Anasayfa
          </Link>
        </div>
      </div>
    );
  }

  const gallery = product.gallery && product.gallery.length > 0
    ? product.gallery
    : (product.hero_image ? [product.hero_image] : []);
  const heroImg = product.hero_image || gallery[0] || "";
  const mainImg = gallery[active] || heroImg;
  const whatsappPhone = settings?.whatsapp_phone ? settings.whatsapp_phone.replace(/\D/g, "") : "";
  const waUrl = settings.whatsapp || (whatsappPhone ? `https://wa.me/${whatsappPhone}?text=${encodeURIComponent(lang === "tr" ? `Merhaba YGS, ${product?.title || ""} hakkında teklif almak istiyorum.` : `Hi YGS, I'd like a quote for ${product?.title || ""}.`)}` : "");

  return (
    <div className="App">
      <Navbar />

      {/* Hero band */}
      <section className="relative bg-[#0d1b2a] text-white overflow-hidden" data-testid="product-detail-hero">
        <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
        {heroImg && (
          <div className="absolute inset-0 opacity-30">
            <img src={resolveImageUrl(heroImg)} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0d1b2a] via-[#0d1b2a]/70 to-transparent" />
          </div>
        )}
        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <nav className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/60" data-testid="breadcrumb">
            <Link to="/" className="hover:text-[#ffb703] transition-colors">{t.nav.home}</Link>
            <ChevronRight size={12} />
            <Link to="/#products" className="hover:text-[#ffb703] transition-colors">{t.nav.products}</Link>
            <ChevronRight size={12} />
            <span className="text-[#ffb703]">{product.title}</span>
          </nav>
          <h1
            className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight"
            style={{ fontFamily: "Chivo, Inter, sans-serif" }}
            data-testid="product-detail-title"
          >
            {product.title}
          </h1>
          <p className="mt-5 text-lg text-white/75 max-w-2xl leading-relaxed">{product.short_desc}</p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              className="btn-primary"
              data-testid="product-detail-cta"
            >
              <MessageCircle size={16} />
              {lang === "tr" ? "WhatsApp ile Teklif Al" : "Get a Quote on WhatsApp"}
            </a>
            {settings.phone && (
              <a
                href={`tel:${settings.phone}`}
                className="inline-flex items-center gap-2 text-white border-2 border-white/25 hover:border-[#ffb703] hover:text-[#ffb703] font-bold px-6 py-3 rounded-md transition-all"
                data-testid="product-detail-phone"
              >
                <Phone size={16} /> {settings.phone || "+90 212 555 0 144"}
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Gallery + sidebar */}
      <section className="py-16 lg:py-24 bg-[#f6f6f6]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Gallery */}
            <div className="lg:col-span-7">
              <div className="aspect-[16/10] bg-[#0d1b2a] rounded-xl overflow-hidden border border-[#0d1b2a]/10" data-testid="product-gallery-main">
                {mainImg ? (
                  <img src={resolveImageUrl(mainImg)} alt={product.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/40 text-sm">
                    Görsel yok
                  </div>
                )}
              </div>
              {gallery.length > 1 && (
                <div className="mt-4 grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {gallery.map((g, i) => (
                    <button
                      key={i}
                      onClick={() => setActive(i)}
                      data-testid={`gallery-thumb-${i}`}
                      className={`aspect-square rounded-md overflow-hidden border-2 transition-all ${
                        i === active ? "border-[#ffb703]" : "border-transparent hover:border-[#0d1b2a]/30"
                      }`}
                    >
                      <img src={resolveImageUrl(g)} alt={`thumb ${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}

              {product.long_desc && (
                <div className="mt-10">
                  <h2
                    className="text-2xl font-black text-[#0d1b2a] mb-4"
                    style={{ fontFamily: "Chivo, Inter, sans-serif" }}
                  >
                    {lang === "tr" ? "Ürün Hakkında" : "About this product"}
                  </h2>
                  <p className="text-base text-[#0d1b2a]/75 leading-relaxed whitespace-pre-line">
                    {product.long_desc}
                  </p>
                </div>
              )}

              {product.features && product.features.length > 0 && (
                <div className="mt-10">
                  <h3
                    className="text-xl font-black text-[#0d1b2a] mb-4"
                    style={{ fontFamily: "Chivo, Inter, sans-serif" }}
                  >
                    {lang === "tr" ? "Öne Çıkan Özellikler" : "Key features"}
                  </h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="product-features">
                    {product.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 bg-white border border-[#0d1b2a]/10 rounded-md p-3 text-sm text-[#0d1b2a]/80">
                        <Check size={16} className="mt-0.5 text-[#ffb703] shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Sidebar: specs */}
            <aside className="lg:col-span-5">
              <div className="sticky top-24 bg-white rounded-xl border border-[#0d1b2a]/10 p-7">
                <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#0d1b2a]/60">
                  {lang === "tr" ? "Teknik Özellikler" : "Specifications"}
                </div>
                <h3
                  className="mt-2 text-2xl font-black text-[#0d1b2a]"
                  style={{ fontFamily: "Chivo, Inter, sans-serif" }}
                >
                  {product.title}
                </h3>
                {product.specs && product.specs.length > 0 ? (
                  <dl className="mt-5 divide-y divide-[#0d1b2a]/10" data-testid="product-specs">
                    {product.specs.map((s, i) => (
                      <div key={i} className="py-3 flex items-baseline justify-between gap-3">
                        <dt className="text-xs uppercase tracking-wider font-bold text-[#0d1b2a]/60 shrink-0">
                          {s.label}
                        </dt>
                        <dd className="text-sm font-bold text-[#0d1b2a] text-right">
                          {s.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <div className="mt-4 text-sm text-[#0d1b2a]/50">
                    {lang === "tr" ? "Detay için iletişime geçin." : "Contact us for details."}
                  </div>
                )}

                <div className="mt-6 p-4 bg-[#0d1b2a] text-white rounded-lg">
                  <div className="text-xs uppercase tracking-wider font-bold text-[#ffb703]">
                    {lang === "tr" ? "Ücretsiz Keşif" : "Free site visit"}
                  </div>
                  <div className="mt-1 text-sm leading-relaxed text-white/80">
                    {lang === "tr"
                      ? "Ekibimiz 24 saat içinde sahada. Şimdi teklif isteyin."
                      : "Our team is on-site within 24h. Request a quote now."}
                  </div>
                  <a
                    href={waUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-primary mt-3 w-full justify-center"
                    data-testid="sidebar-quote-cta"
                  >
                    <MessageCircle size={14} />
                    {lang === "tr" ? "WhatsApp ile Teklif Al" : "Get a Quote on WhatsApp"}
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Related products */}
      {relatedProducts.length > 0 && (
        <section className="py-16 bg-white" data-testid="related-products">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#0d1b2a]/60">
                  {lang === "tr" ? "Diğer ürünler" : "Other products"}
                </div>
                <h2 className="mt-2 text-2xl sm:text-3xl font-black text-[#0d1b2a]" style={{ fontFamily: "Chivo, Inter, sans-serif" }}>
                  {lang === "tr" ? "Tüm sistemlerimizi keşfedin" : "Explore all our systems"}
                </h2>
              </div>
              <Link to="/#products" className="btn-secondary">
                {lang === "tr" ? "Tümünü Gör" : "View all"} <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedProducts.slice(0, 3).map((p) => (
                <Link
                  to={`/urun/${p.slug}`}
                  key={p.id}
                  data-testid={`related-product-${p.slug}`}
                  className="ygs-card overflow-hidden flex flex-col group"
                >
                  <div className="aspect-[4/3] bg-[#0d1b2a] overflow-hidden">
                    {p.hero_image && (
                      <img
                        src={resolveImageUrl(p.hero_image)}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-[#0d1b2a]" style={{ fontFamily: "Chivo, Inter, sans-serif" }}>
                      {p.title}
                    </h3>
                    <p className="mt-2 text-sm text-[#0d1b2a]/70 line-clamp-2">{p.short_desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Contact settings={settings} />
      <Footer settings={settings} />
      <WhatsAppFloat settings={settings} />
    </div>
  );
}
